/**
 * PlaybackScreen — renders the current playback item.
 *
 * Displays either an image or a video depending on the current item's type.
 * For videos, uses the VideoPlayer with dual-element seamless loop.
 * For images, uses ImageDisplay for the configured duration.
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import type { PlaybackItem } from './schedule-evaluator';
import { VideoPlayer } from './VideoPlayer';
import { ImageDisplay } from './ImageDisplay';
import { getAssetPlaybackUrl, markAssetActive } from '../cache';
import type { CachedAssetMeta } from '../cache/types';

export interface PlaybackScreenProps {
  /** The current item to display */
  item: PlaybackItem | null;
  /** Current playback mode */
  mode: 'scheduled' | 'fallback' | 'emergency' | 'idle';
  /** Asset metadata map for getting playback URLs */
  assetMetaMap: Map<string, CachedAssetMeta>;
  /** Called when a video finishes (for non-looping or fallback advance) */
  onVideoEnded?: () => void;
  /**
   * Called when the current image fails to load (#173). The parent advances
   * to the next item so a broken image doesn't hold a black frame for its
   * full slot duration.
   */
  onImageError?: () => void;
  /**
   * Called when the current image successfully loads (#173). Used by the
   * parent to gate proof-of-play so only rendered images bill an impression.
   */
  onImageLoad?: () => void;
  /** When true, suspends video playback (e.g. recycle overlay is visible). */
  paused?: boolean;
}

export function PlaybackScreen({
  item,
  mode,
  assetMetaMap,
  onVideoEnded,
  onImageError,
  onImageLoad,
  paused = false,
}: PlaybackScreenProps) {
  const [assetUrl, setAssetUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Whether the loading UI (spinner + "Downloading content…") is visible.
  // Gated behind a short delay so fast cache hits transition through plain
  // black instead of flashing a spinner between every item, while genuine
  // downloads show a clear status rather than looking like a broken screen.
  const [showLoadingUi, setShowLoadingUi] = useState(false);
  const prevItemIdRef = useRef<string | null>(null);
  const prevMetaRef = useRef<CachedAssetMeta | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  /** Delay before an unresolved asset shows the downloading indicator. */
  const LOADING_UI_DELAY_MS = 400;

  const metaMissing = !!item && !assetMetaMap.has(item.assetId);
  const isLoading = metaMissing || !assetUrl;
  useEffect(() => {
    if (!isLoading) {
      setShowLoadingUi(false);
      return;
    }
    const timer = setTimeout(() => setShowLoadingUi(true), LOADING_UI_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Resolve the asset URL when the item changes
  useEffect(() => {
    const itemId = item?.assetId ?? null;
    const meta = itemId ? assetMetaMap.get(itemId) ?? null : null;

    // Playback streams from the OPFS file on disk, so eviction must not
    // delete the asset that is on screen right now.
    markAssetActive(itemId);

    // Skip only when nothing that affects URL resolution changed: same item,
    // same storage backend + verification, and a URL already resolved. A
    // late-registered emergency asset that finishes downloading flips
    // checksumVerified (and can move cache-storage→opfs), so those deltas
    // must fall through to re-resolve the URL from the remote stream to the
    // local copy (#71).
    if (
      itemId === prevItemIdRef.current &&
      meta?.storageBackend === prevMetaRef.current?.storageBackend &&
      meta?.checksumVerified === prevMetaRef.current?.checksumVerified &&
      assetUrl !== null
    ) {
      return;
    }
    prevItemIdRef.current = itemId;
    prevMetaRef.current = meta;

    // Revoke previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    if (!item) {
      setAssetUrl(null);
      setError(null);
      return;
    }

    if (!meta) {
      setError(`Asset metadata not found: ${item.assetId}`);
      setAssetUrl(null);
      return;
    }

    let cancelled = false;

    getAssetPlaybackUrl(meta).then((url) => {
      if (cancelled) {
        // The item/meta changed (or the component unmounted) before this
        // resolved. A blob URL minted for an OPFS asset here was never assigned
        // to blobUrlRef, so neither the top-of-effect revoke nor the unmount
        // cleanup would ever release it — revoke it now to avoid the leak (#194).
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
        return;
      }
      if (url) {
        // Track blob URLs for cleanup. Any previous blob URL was already
        // revoked at the top of this effect run before re-resolution.
        if (url.startsWith('blob:')) {
          blobUrlRef.current = url;
        }
        setAssetUrl(url);
        setError(null);
      } else {
        setError(`Failed to get playback URL for: ${item.assetId}`);
        setAssetUrl(null);
      }
    });

    return () => {
      cancelled = true;
    };
    // assetMetaMap is a dep so a late-arriving or upgraded meta record (e.g.
    // an emergency asset registered after the command, then finishing its
    // background download, #71) re-resolves the URL; the guard above skips
    // re-resolution only when no URL-affecting field changed.
  }, [item?.assetId, assetMetaMap]);

  // Cleanup blob URLs + active-asset eviction guard on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      markAssetActive(null);
    };
  }, []);

  const handleVideoEnded = useCallback(() => {
    onVideoEnded?.();
  }, [onVideoEnded]);

  // ─── Unresolved metadata ─────────────────────────────────────────
  // If the schedule references an asset whose metadata hasn't loaded yet
  // (reboot / SW-build swap / cache rebuild), the evaluator can't know its
  // type and defaults to 'image'. Rendering a video URL through <img> fails
  // instantly and the parent advances — producing a rapid flash through the
  // whole slot. Show the downloading status instead until meta arrives.
  if (item && !assetMetaMap.has(item.assetId)) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-black">
        {showLoadingUi && (
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            <p className="text-gray-400 text-sm">Downloading content…</p>
          </div>
        )}
      </div>
    );
  }

  // ─── Idle / no item state ────────────────────────────────────────

  if (!item || mode === 'idle') {
    return (
      <div className="flex items-center justify-center w-full h-full bg-black">
        <div className="text-center">
          <p className="text-gray-500 text-sm">
            {mode === 'idle' ? 'Initializing...' : 'No content available'}
          </p>
        </div>
      </div>
    );
  }

  // ─── Error state ─────────────────────────────────────────────────

  if (error) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-black">
        <div className="text-center">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // ─── Loading state ───────────────────────────────────────────────

  if (!assetUrl) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-black">
        {/* Plain black for the first LOADING_UI_DELAY_MS so quick cache hits
            don't flash a spinner; longer waits (real downloads) show a clear
            status so the screen doesn't look broken. */}
        {showLoadingUi && (
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            <p className="text-gray-400 text-sm">Downloading content…</p>
          </div>
        )}
      </div>
    );
  }

  // The item's assetType can be stale: the schedule evaluator computes it once
  // and defaults to 'image' when asset meta hasn't loaded yet, so an item
  // captured during that window keeps 'image' even after the (video) meta
  // arrives. Trust the live cached meta over the item's snapshot so a video
  // never renders through <img> (which fails instantly and flashes).
  const liveMeta = assetMetaMap.get(item.assetId);
  const effectiveType = liveMeta?.assetType ?? item.assetType;

  // ─── Video playback ──────────────────────────────────────────────

  if (effectiveType === 'video') {
    return (
      <VideoPlayer
        src={assetUrl}
        loop={mode === 'scheduled'}
        onEnded={handleVideoEnded}
        className="w-full h-full"
        paused={paused}
      />
    );
  }

  // ─── Image display ───────────────────────────────────────────────

  return (
    <ImageDisplay
      src={assetUrl}
      duration={item.duration}
      className="w-full h-full"
      onLoad={onImageLoad}
      onError={onImageError}
    />
  );
}
